/**
 * A single account's margin trend, small enough for a table cell.
 *
 * Direction, not level -- the number beside it carries the level. No axis, no
 * labels: at this size they would be noise, and the row's own cells say what
 * the value is. The endpoint dot marks the latest reading so the eye lands
 * where the row's figure comes from.
 */
import { SERIES_1 } from "./chart-primitives";

export function Sparkline({
  values,
  width = 88,
  height = 22,
  label,
}: {
  values: (number | null)[];
  width?: number;
  height?: number;
  label?: string;
}) {
  const present = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (present.length < 2) {
    return <span className="text-xs text-muted-foreground">&mdash;</span>;
  }

  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  // Inset by the dot radius so the endpoint is never clipped at the top or bottom.
  const inset = 2.5;
  const y = (v: number) => inset + (height - 2 * inset) * (1 - (v - min) / span);

  const segments: string[][] = [];
  let run: string[] = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (run.length > 1) segments.push(run);
      run = [];
      return;
    }
    run.push(`${i * step},${y(v)}`);
  });
  if (run.length > 1) segments.push(run);

  let lastIndex = values.length - 1;
  while (lastIndex >= 0 && values[lastIndex] === null) lastIndex -= 1;
  const lastValue = values[lastIndex] as number;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? "Margin trend"}
      className="overflow-visible"
    >
      {segments.map((seg) => (
        <polyline
          key={seg[0]}
          fill="none"
          stroke={SERIES_1}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={seg.join(" ")}
        />
      ))}
      <circle data-spark-end cx={lastIndex * step} cy={y(lastValue)} r={2.5} fill={SERIES_1} />
    </svg>
  );
}
